import { LANG } from "./symbols";
import { format, addMinutes, parse } from "date-fns";

export const MONTH_NAMES_LONG = Array.from({ length: 12 }, (_, month) =>
	new Date(2024, month, 3).toLocaleDateString(LANG, { month: "long" })
);
export const MONTH_NAMES_SHORT = Array.from({ length: 12 }, (_, month) =>
	new Date(2024, month, 3).toLocaleDateString(LANG, { month: "short" })
);
export const DAY_NAMES_LONG = Array.from({ length: 7 }, (_, offset) =>
	new Date(2024, 0, 7 + offset).toLocaleDateString(LANG, { weekday: "long" })
);
export const DAY_NAMES_SHORT = Array.from({ length: 7 }, (_, offset) =>
	new Date(2024, 0, 7 + offset).toLocaleDateString(LANG, { weekday: "short" })
);

export const getAdjustedDay = (date, firstDayOfWeek) =>
	(date.getDay() - (firstDayOfWeek ?? 1) + 7) % 7;

export const parseDate = (format, date, utc) => {
	try {
		const dateObj = new Date(date);
		let parsedDate = "";
		if (isNaN(dateObj.getTime()))
			throw new Error(`Not possible to build a date object with ${date}`);

		const WEEK_DAY = utc ? dateObj.getUTCDay() : dateObj.getDay();
		const DATE = utc ? dateObj.getUTCDate() : dateObj.getDate();
		const MONTH = utc ? dateObj.getUTCMonth() : dateObj.getMonth();
		const YEAR = utc ? dateObj.getUTCFullYear() : dateObj.getFullYear();
		const HOURS = utc ? dateObj.getUTCHours() : dateObj.getHours();
		const MINUTES = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes();
		const SECONDS = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes();

		switch (format) {
			case "Mon":
				parsedDate = DAY_NAMES_SHORT[WEEK_DAY];
				break;
			case "d":
				parsedDate = String(DATE);
				break;
			case "November yyyy":
				parsedDate = `${MONTH_NAMES_LONG[MONTH]} ${YEAR}`;
				break;
			case "November":
				parsedDate = `${MONTH_NAMES_LONG[MONTH]}`;
				break;
			case "Nov":
				parsedDate = MONTH_NAMES_SHORT[MONTH];
				break;
			case "d Nov yyyy":
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} ${YEAR}`;
				break;
			case "Mon d November yyyy":
				parsedDate = `${DAY_NAMES_SHORT[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`;
				break;
			case "Monday d November yyyy":
				parsedDate = `${DAY_NAMES_LONG[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`;
				break;
			case "d Nov 'yy":
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} '${String(
					YEAR
				).substring(2)}`;
				break;
			case "yyyy-mm-dd":
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(
					2,
					"0"
				)}-${String(DATE).padStart(2, "0")}`;
				break;
			case "yyyy-mm-dd hh:mm:ss":
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(
					2,
					"0"
				)}-${String(DATE).padStart(2, "0")} ${String(HOURS).padStart(
					2,
					"0"
				)}:${String(MINUTES).padStart(2, "0")}:${String(
					SECONDS
				).padStart(2, "0")}`;
				break;
			case "hh:mm":
				parsedDate = `${String(HOURS).padStart(2, "0")}:${String(
					MINUTES
				).padStart(2, "0")}`;
				break;
			default:
				break;
		}

		return parsedDate;
	} catch (error) {
		return "Invalid Date";
	}
};

export const getWeekSlotsTillMonthEnd = (date, firstDayOfWeek) => {
	const currentDay = date.getDate();
	const firstWeekDay = getAdjustedDay(date);
	const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
	const lastWeekDay = getAdjustedDay(lastDay);
	const remainingDays = lastDay.getDate() - currentDay + 1;
	const totalWeekSlots = remainingDays + firstWeekDay + (6 - lastWeekDay);
	return Math.ceil(totalWeekSlots / 7);
};

export const getMonthDifference = (date1, date2) => {
	if (date1 === undefined || date2 === undefined) return 0;
	const firstDate = new Date(date1);
	const secondDate = new Date(date2);
	if (
		isNaN(firstDate.getTime()) ||
		isNaN(secondDate.getTime()) ||
		date1 === 0 ||
		date2 === 0
	)
		return 0;
	const year1 = firstDate.getFullYear();
	const month1 = firstDate.getMonth();
	const year2 = secondDate.getFullYear();
	const month2 = secondDate.getMonth();
	const yearDifference = year2 - year1;
	const monthDifference = month2 - month1;
	return yearDifference * 12 + monthDifference;
};

export function orderCentersByDistance(centers, coords) {
	return centers
		.map((center) => addDistanceToCenter(center, coords))
		.sort((a, b) => a.distance - b.distance);
}

export function addDistanceToCenter(center, coords) {
	if (!coords) {
		return center;
	}
	const centerCoords = {
		lat: center.latitude,
		lng: center.longitude,
	};
	return {
		...center,
		distance: haversineDistance(coords, centerCoords),
	};
}

function haversineDistance(coord1, coord2) {
	const R = 6371;
	const dLat = toRadians(coord2.lat - coord1.lat);
	const dLon = toRadians(coord2.lng - coord1.lng);
	const lat1 = toRadians(coord1.lat);
	const lat2 = toRadians(coord2.lat);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

function toRadians(deg) {
	return (deg * Math.PI) / 180;
}

export function formatRange(time, offsetStart, offsetEnd) {
	const baseTime = parse(time, "HH:mm", new Date());
	const start = format(addMinutes(baseTime, offsetStart), "HH:mm");
	const end = format(addMinutes(baseTime, offsetEnd), "HH:mm");
	return `${start} - ${end}`;
}

export function sendEventToParent(eventType, data, FLOW) {
	console.log(`Sending event to parent: ${eventType} with data:`, {
		status: eventType,
		appointment: data.code || null,
		datatime: data.date
			? data.time
				? `${data.date}T${data.time}`
				: data.date
			: null,
		branch: {
			id: data.centerCode || null,
			name: data.centerName || null,
		},
	});
	if (FLOW === "mapfre") {
		window.parent.postMessage(
			{
				type: eventType,
				data: {
					status: eventType,
					appointment: data.code || null,
					datatime: data.date
						? data.time
							? `${data.date}T${data.time}`
							: data.date
						: null,
					branch: {
						id: data.centerCode || null,
						name: data.centerName || null,
					},
				},
			},
			"*"
		);
	}
}
